/**
 * Profilo → «Notifiche sul telefono» (Andrea 2026-10-16).
 *
 * - Interruttore grande: attiva/disattiva le push su QUESTO dispositivo
 *   (permesso del browser + subscription registrata sul backend).
 * - Interruttori per categoria (mercato, giornata calcolata, promemoria
 *   formazione, Squad Magazine): salvati sul profilo, valgono per tutti i
 *   dispositivi dell'utente. Mercato e giornata accesi di default, gli altri
 *   spenti (Andrea: poche push).
 * - «Mandami una notifica di prova» per vedere che arriva davvero.
 */

import { useState } from "react";
import { api } from "../../lib/api";
import { useAuthStore, type User } from "../../lib/auth-store";
import {
  attivaPush,
  disattivaPush,
  PermessoNegatoError,
  preferenzePush,
  type PreferenzePush,
  usePushDispositivo,
} from "../../lib/push";
import { tr } from "../../i18n";

type Esito = { ok: boolean; testo: string } | null;

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return (
    (typeof err.response?.data?.detail === "string" && err.response.data.detail) ||
    err.message ||
    tr("Errore")
  );
}

function Interruttore({
  acceso,
  onChange,
  disabled,
  etichetta,
}: {
  acceso: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  etichetta: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={acceso}
      aria-label={etichetta}
      disabled={disabled}
      onClick={() => onChange(!acceso)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
        acceso ? "bg-accent" : "bg-primary-soft"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          acceso ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

function Riga({
  titolo,
  descrizione,
  acceso,
  onChange,
  disabled,
}: {
  titolo: string;
  descrizione: string;
  acceso: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-fg">{titolo}</div>
        <div className="text-[11px] text-fg-muted leading-snug">{descrizione}</div>
      </div>
      <Interruttore acceso={acceso} onChange={onChange} disabled={disabled} etichetta={titolo} />
    </div>
  );
}

const CATEGORIE: { chiave: keyof PreferenzePush; titolo: string; descrizione: string }[] = [
  {
    chiave: "mercato",
    titolo: "Mercato",
    descrizione: "Proposte, controproposte, accettazioni e rifiuti delle tue trattative",
  },
  {
    chiave: "giornata",
    titolo: "Giornata calcolata",
    descrizione: "Il risultato della tua partita appena i punteggi sono pronti",
  },
  {
    chiave: "formazione",
    titolo: "Promemoria formazione",
    descrizione: "Un'ora prima della chiusura, solo se non hai ancora consegnato",
  },
  {
    chiave: "stampa",
    titolo: "Squad Magazine",
    descrizione: "Prime pagine, report, Top 11 e quando ti nominano",
  },
];

export function SezioneNotifichePush({ user }: { user: User }) {
  const setUser = useAuthStore((s) => s.setUser);
  const { supporto, attive, permesso, aggiorna } = usePushDispositivo();
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<Esito>(null);
  const scelte = preferenzePush(user.preferenze_notifiche);

  const cambiaDispositivo = async (v: boolean) => {
    setInCorso(true);
    setEsito(null);
    try {
      if (v) {
        await attivaPush();
        setEsito({ ok: true, testo: tr("Notifiche attive su questo dispositivo") });
      } else {
        await disattivaPush();
        setEsito({ ok: true, testo: tr("Notifiche disattivate su questo dispositivo") });
      }
    } catch (e) {
      if (e instanceof PermessoNegatoError) {
        setEsito({
          ok: false,
          testo: tr(
            "Permesso negato: riattivalo dalle impostazioni del telefono (Notifiche → FantaSquad o il browser) e riprova.",
          ),
        });
      } else {
        setEsito({ ok: false, testo: errMsg(e) });
      }
    } finally {
      await aggiorna();
      setInCorso(false);
    }
  };

  const cambiaCategoria = async (chiave: keyof PreferenzePush, v: boolean) => {
    setEsito(null);
    try {
      const res = await api.patch<User>("/auth/me", {
        preferenze_notifiche: { push: { ...scelte, [chiave]: v } },
      });
      setUser(res.data);
    } catch (e) {
      setEsito({ ok: false, testo: errMsg(e) });
    }
  };

  const prova = async () => {
    setInCorso(true);
    setEsito(null);
    try {
      const res = await api.post<{ inviate: number; fallite: number; rimosse: number }>(
        "/push/test",
      );
      if (res.data.inviate > 0) {
        setEsito({
          ok: true,
          testo: tr("Inviata a {{v1}} dispositivi: controlla il telefono", {
            v1: res.data.inviate,
          }),
        });
      } else {
        setEsito({
          ok: false,
          testo: tr("Non e' partita: riattiva le notifiche su questo dispositivo e riprova"),
        });
      }
      await aggiorna();
    } catch (e) {
      setEsito({ ok: false, testo: errMsg(e) });
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
        {tr("Notifiche sul telefono")}
      </div>

      {supporto === "ios_browser" && (
        <div className="text-xs text-fg-muted leading-relaxed">
          {tr(
            "Su iPhone e iPad le notifiche funzionano solo con l'app aggiunta alla schermata Home da Safari: installala (vedi sopra) e torna qui.",
          )}
        </div>
      )}
      {supporto === "no" && (
        <div className="text-xs text-fg-muted">
          {tr("Questo browser non supporta le notifiche push.")}
        </div>
      )}

      {supporto === "ok" && (
        <Riga
          titolo={tr("Attive su questo dispositivo")}
          descrizione={tr("Il telefono riceve un avviso anche con l'app chiusa.")}
          acceso={attive === true}
          onChange={cambiaDispositivo}
          disabled={inCorso || attive === null}
        />
      )}
      {supporto === "ok" && permesso === "denied" && attive === false && (
        <div className="text-[11px] text-danger leading-snug">
          {tr(
            "Permesso negato: riattivalo dalle impostazioni del telefono (Notifiche → FantaSquad o il browser) e riprova.",
          )}
        </div>
      )}

      <div className="border-t border-primary-soft pt-3 space-y-3">
        <div className={LABEL}>{tr("Cosa ricevere")}</div>
        {CATEGORIE.map((c) => (
          <Riga
            key={c.chiave}
            titolo={tr(c.titolo)}
            descrizione={tr(c.descrizione)}
            acceso={scelte[c.chiave]}
            onChange={(v) => cambiaCategoria(c.chiave, v)}
          />
        ))}
        <div className="text-[11px] text-fg-muted">
          {tr("Le scelte valgono per tutti i tuoi dispositivi.")}
        </div>
      </div>

      {supporto === "ok" && (
        <button
          type="button"
          className="btn-ghost w-full disabled:opacity-50"
          disabled={inCorso || attive !== true}
          onClick={prova}
        >
          {inCorso ? tr("Un attimo…") : tr("Mandami una notifica di prova")}
        </button>
      )}
      {esito && (
        <div className={`text-xs ${esito.ok ? "text-success" : "text-danger"}`}>
          {esito.ok ? "✓ " : "⚠ "}
          {esito.testo}
        </div>
      )}
    </div>
  );
}

const LABEL = "block text-xs uppercase tracking-wider text-fg-muted";
